import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { SubService } from 'src/app/services/sub.service';
import { BeUsuario } from 'src/app/models/BeUsuario';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { TokenService } from 'src/app/services/token.service';
import { AuthService } from 'src/app/services/auth.service';
import { LevelPte } from 'src/app/app-config';
import { MatDialog } from '@angular/material/dialog';
import { DialogFotoPerfilComponent } from 'src/app/components/dialog-foto-perfil/dialog-foto-perfil.component';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';

@Component({
  selector: 'app-edit-usuario',
  templateUrl: './edit-usuario.component.html',
  styleUrls: ['./edit-usuario.component.css']
})
export class EditUsuarioComponent implements OnInit, OnDestroy {
  public hide = true;
  public idUsuario?: number;
  public usuario?: BeUsuario;
  public subscription?: Subscription;
  public editFormGroup: FormGroup;
  public exibindoRegrasETokens = false;
  public exibeCamposEasyMob = false;
  public fotoPerfilDisponivel = true;
  public fotoPerfilCarregada = false;
  public fotoPerfilVersao = Date.now();

  private editIdUsuarioFormControl = new FormControl<number | null>(null, [Validators.required]);
  private editNoNomeCompletoFormControl = new FormControl<string>("", [Validators.required, Validators.minLength(5)]);
  private editNoCpfjFormControl = new FormControl<string | null>(null);
  private editNoSenhaFormControl = new FormControl<string | null>({ value: null, disabled: false });
  private editNoEmailFormControl = new FormControl<string>("", [Validators.required, Validators.minLength(5)]);
  private editNrCelularFormControl = new FormControl<string | null>(null);
  private editNoEasyMobChaveFormControl = new FormControl<string | null>(null);
  private editNoEasyMobUsuarioFormControl = new FormControl<string | null>(null);
  private editNoEasyMobSenhaFormControl = new FormControl<string | null>(null);

  public constructor(
    private httpClient: HttpClient,
    private route: ActivatedRoute,
    private sub: SubService,
    private router: Router,
    private snackBarService: SnackBarService,
    private tokenService: TokenService,
    private dialog: MatDialog,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService
  ) {
    this.editFormGroup = new FormGroup({
      idUsuario: this.editIdUsuarioFormControl,
      noNomeCompleto: this.editNoNomeCompletoFormControl,
      noSenha: this.editNoSenhaFormControl,
      noCpfj: this.editNoCpfjFormControl,
      noEmail: this.editNoEmailFormControl,
      nrCelular: this.editNrCelularFormControl,
      noEasyMobChave: this.editNoEasyMobChaveFormControl,
      noEasyMobUsuario: this.editNoEasyMobUsuarioFormControl,
      noEasyMobSenha: this.editNoEasyMobSenhaFormControl,
    });
  }

  public ngOnInit(): void {
    this.sub.clear();
    this.idUsuario = Number(this.route.snapshot.paramMap.get('id'));
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao())
      this.exibindoRegrasETokens = sessionStorage.getItem(`usuarios.${this.idUsuario}.modoExibicao`) === 'regras-etokens';
    else
      sessionStorage.setItem(`usuarios.${this.idUsuario}.modoExibicao`, 'edicao');
    if (this.idUsuario) {
      this.subscription = this.httpClient.get<BeUsuario>(`/api/Usuarios/${this.idUsuario}`).subscribe(usuario => {
        this.editIdUsuarioFormControl.setValue(usuario.idUsuario ?? 0);
        this.editNoNomeCompletoFormControl.setValue(usuario.noNomeCompleto);
        this.editNoCpfjFormControl.setValue(usuario.noCpfj);
        this.editNoEmailFormControl.setValue(usuario.noEmail);
        this.editNrCelularFormControl.setValue(usuario.nrCelular ?? null);
        this.editNoEasyMobChaveFormControl.setValue(usuario.noEasyMobChave ?? null);
        this.editNoEasyMobUsuarioFormControl.setValue(usuario.noEasyMobUsuario ?? null);
        this.editNoEasyMobSenhaFormControl.setValue(usuario.noEasyMobSenha ?? null);
        this.usuario = usuario;
        this.fotoPerfilDisponivel = !!usuario.idImagem;
        this.unsubscribe();
      });
    }

    this.tokenService
      .getTokenUserCredentials()
      .subscribe((token) =>
        this.tokenService
          .decodeToken(token)
          .subscribe((authToken) => {
            this.exibeCamposEasyMob = AuthService.canTokenActivate(authToken, LevelPte);
          })
      );
  }

  public ngOnDestroy(): void {
    this.sub.clear();
    this.unsubscribe();
  }

  public onSalvar() {
    this.unsubscribe();
    const payload = { ...this.editFormGroup.value };
    if (payload.nrCelular) {
      payload.nrCelular = payload.nrCelular.replace(/\D/g, '');
    }
    this.subscription = this.httpClient.put<BeUsuario>(
      `/api/Usuarios`, payload
    ).subscribe({
      next: () => {
        this.unsubscribe();
        this.snackBarService.exibirSucesso('Registro salvo!');
        this.router.navigate(['/usuarios'], { relativeTo: this.route });
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  public exibirEdicao(): void {
    this.exibindoRegrasETokens = false;
    sessionStorage.setItem(`usuarios.${this.idUsuario}.modoExibicao`, 'edicao');
  }

  public exibirRegrasETokens(): void {
    this.exibindoRegrasETokens = true;
    sessionStorage.setItem(`usuarios.${this.idUsuario}.modoExibicao`, 'regras-etokens');
  }

  public abrirFotoPerfil(): void {
    if (!this.idUsuario) return;
    this.dialog.open(DialogFotoPerfilComponent, {
      data: { idUsuario: this.idUsuario },
      autoFocus: false,
      width: '680px',
      maxWidth: 'calc(100vw - 32px)'
    }).afterClosed().subscribe(alterou => {
      if (alterou) {
        this.fotoPerfilDisponivel = true;
        this.fotoPerfilCarregada = false;
        this.fotoPerfilVersao = Date.now();
      }
    });
  }

  public onErroFotoPerfil(): void {
    this.fotoPerfilDisponivel = false;
    this.fotoPerfilCarregada = false;
  }

  public onCarregouFotoPerfil(): void {
    this.fotoPerfilCarregada = true;
  }

  public iniciaisUsuario(): string {
    return (this.usuario?.noNomeCompleto ?? '?').trim().split(/\s+/).slice(0, 2).map(nome => nome[0]).join('').toUpperCase();
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

}
